import { useRef, useState } from 'react';
import { useRouter } from 'next/router';

import { Box, LoadingMask, Paragraph, Text, TextButton } from '@zillow/constellation-10';

import { useGetListingDetails, useGetListingRecommendations, useGetTours } from '../../../hooks/queries/properties';
import { useAiImageListingCompliance } from '../../../hooks/queries/useAiImageListingCompliance';
import { useQrPhotoUploadEntryPoint } from '../../../hooks/useQrPhotoUploadEntryPoint';

import { MEDIA_CREDITS_COOKIES } from '../../../constants/CookieConstants';
import { REVIEW_PAGE_SECTIONS } from '../../../constants/ListingDetailConstants';
import { RECS } from '../../../constants/RecommendationsConstants';

import RecommendationModal from '../../modals/RecommendationModal';

import { iconPlaceholderStyles, reviewSectionStyles, reviewSectionTitleStyles, reviewTitleRowStyles } from './styles';

import { emitLearnMoreEvent, EVENT_IDS } from '../../../analytics/events/add3DTours';
import { useAuth } from '../../AuthProvider';
import MediaCreditsBanner from '../../MediaCreditsBanner';
import RecommendationIconBulb from '../RecommendationIconBulb';
import RecommendationsAlert from '../RecommendationsAlert';
import DigitallyAlteredPhotosAlert from './DigitallyAlteredPhotosAlert';
import ListingTourFormField from './ListingTourFormField';
import PhotoUploadFormField from './PhotoUploadFormField';
import VideoFormField from './VideoFormField';

import type { ZrmClickstreamUser } from '@zg-rentals/clickstream';
import type { PhotoGalleryHandle } from './PhotoUploadFormField/components/PhotoGallery';

type Props = {
  onRecsClick?: (recType: string) => void;
};

const MediaContent = ({ onRecsClick }: Props) => {
  const router = useRouter();
  const listingAlias = router.query.listingAlias as string;
  const { user } = useAuth();

  const { data: listingDetails, isFetching: isFetchingListingDetails } = useGetListingDetails(listingAlias);
  const { recommendationsData, recommendationsBySection } = useGetListingRecommendations({
    listingAlias,
    isMultifamily: listingDetails?.isMultifamily,
  });
  const { isLoading: isLoadingTours } = useGetTours(listingAlias, listingDetails);
  const { isEnabled: showDigitallyAlteredPhotosAlert, isAttestationCompleted } = useAiImageListingCompliance({
    listingAlias,
  });

  const isMultifamily = listingDetails?.isMultifamily ?? false;
  const isLoading = isFetchingListingDetails || isLoadingTours;

  const recommendationTypes = new Set(recommendationsData?.recommendations?.map((r) => r.type) ?? []);

  const [displayRecsModal, setDisplayRecsModal] = useState<boolean | string>(false);
  const photoGalleryRef = useRef<PhotoGalleryHandle>(null);
  const renderPhoneUpload = useQrPhotoUploadEntryPoint(listingAlias, isLoading);

  return (
    <LoadingMask.Root loading={isLoading} css={{ width: '100%' }}>
      <LoadingMask.Content css={{ width: '100%' }}>
        <Box data-testid="MediaContent" data-group-title="Media">
          {isMultifamily && <RecommendationsAlert sectionName={REVIEW_PAGE_SECTIONS.MEDIA} onRecsClick={onRecsClick} />}

          <MediaCreditsBanner cookieKey={MEDIA_CREDITS_COOKIES.HAS_CLOSED_SECTION_BANNER} />

          {/* Photos Section */}
          <Box css={reviewSectionStyles} data-testid="AddPhotos">
            <Box css={{ display: 'flex', alignItems: 'center', marginBottom: '400' }}>
              {!isMultifamily && (
                <Box css={iconPlaceholderStyles}>
                  {recommendationsBySection[REVIEW_PAGE_SECTIONS.MEDIA]?.[RECS.PHOTOS] && (
                    <RecommendationIconBulb
                      label="photos"
                      onClick={() => {
                        setDisplayRecsModal(RECS.PHOTOS);
                      }}
                    />
                  )}
                </Box>
              )}
              <Box css={reviewSectionTitleStyles}>
                <Text textStyle="body-bold">{isMultifamily ? 'Building photos' : 'Photos'}</Text>
                {!isMultifamily && recommendationTypes.has(RECS.PHOTOS) && (
                  <TextButton textStyle="body" onClick={() => setDisplayRecsModal(RECS.PHOTOS)}>
                    Learn more
                  </TextButton>
                )}
              </Box>
            </Box>

            {showDigitallyAlteredPhotosAlert && (
              <Box css={{ display: 'flex' }}>
                {!isMultifamily && <Box css={iconPlaceholderStyles} />}
                <Box css={{ flex: 1, minWidth: 0 }}>
                  <DigitallyAlteredPhotosAlert
                    listingAlias={listingAlias}
                    isAttestationCompleted={isAttestationCompleted}
                    hasPhotos={Boolean(listingDetails?.propertyPhotos?.length)}
                    onReviewPhotos={() => photoGalleryRef.current?.openPhotoDetails(0)}
                  />
                </Box>
              </Box>
            )}

            <Box css={reviewTitleRowStyles}>
              {!isMultifamily && <Box css={iconPlaceholderStyles} />}
              <PhotoUploadFormField
                emptyStateLayout="panels"
                renderPhoneUpload={renderPhoneUpload}
                galleryHandleRef={photoGalleryRef}
              />
            </Box>
          </Box>

          {/* Tours Section */}
          <Box css={isMultifamily ? reviewSectionStyles : undefined} data-testid="AddListingTour">
            <Box css={{ display: 'flex', alignItems: 'center', marginBottom: '200' }}>
              {!isMultifamily && (
                <Box css={iconPlaceholderStyles}>
                  {recommendationsBySection[REVIEW_PAGE_SECTIONS.MEDIA]?.[RECS.VIRTUAL_TOUR] && (
                    <RecommendationIconBulb
                      label="the virtual tour"
                      dataEventid={EVENT_IDS.LEARN_MORE}
                      onClick={() => {
                        emitLearnMoreEvent({ user, currentProperty: listingDetails });
                        setDisplayRecsModal(RECS.VIRTUAL_TOUR);
                      }}
                    />
                  )}
                </Box>
              )}
              <Box css={reviewSectionTitleStyles}>
                <Text textStyle="body-bold">{isMultifamily ? 'Amenity tours' : '3D Tour'}</Text>
                {!isMultifamily && recommendationTypes.has(RECS.VIRTUAL_TOUR) && (
                  <TextButton
                    textStyle="body"
                    data-event-id={EVENT_IDS.LEARN_MORE}
                    onClick={() => {
                      emitLearnMoreEvent({ user, currentProperty: listingDetails });
                      setDisplayRecsModal(RECS.VIRTUAL_TOUR);
                    }}
                  >
                    Learn more
                  </TextButton>
                )}
              </Box>
            </Box>

            <Box css={{ display: 'flex' }}>
              {!isMultifamily && <Box css={iconPlaceholderStyles} />}
              <ListingTourFormField />
            </Box>
          </Box>

          {/* Video Section (multifamily only) */}
          {isMultifamily && (
            <Box data-testid="AddVideoLink">
              <Paragraph textStyle="body-bold" css={{ marginBottom: '400' }}>
                Video
              </Paragraph>
              <VideoFormField />
            </Box>
          )}

          {typeof displayRecsModal === 'string' && (
            <RecommendationModal
              onClose={() => setDisplayRecsModal(false)}
              recommendationType={displayRecsModal}
              listing={listingDetails}
              user={user as ZrmClickstreamUser}
            />
          )}
        </Box>
      </LoadingMask.Content>
      <LoadingMask.Spinner />
    </LoadingMask.Root>
  );
};

export default MediaContent;
